/**
 * AI assistant — wire contract for the email-builder chat endpoint
 * (`POST /v1/ai/email-builder/chat`).
 *
 * These types mirror, field for field, the backend's
 * `modules/ai/agents/email-builder.operations.ts` and the agent's inferred
 * `EmailBuilderUIMessage`. They are duplicated here because the two apps are
 * separate packages — keep them in sync when the backend contract changes.
 */

import type { UIDataTypes, UIMessage } from 'ai';
import type {
  EmailBlockData,
  RenderEmailRequest,
} from '@/api/email-builder/email-builder.types.ts';

/** Path of the streaming email-builder chat endpoint. */
export const EMAIL_BUILDER_CHAT_PATH = '/v1/ai/email-builder/chat';

/**
 * The builder draft sent with every chat request. The agent reads it as
 * context, so it must reflect the *current* editor state on each turn.
 */
export type EmailBuilderDraft = RenderEmailRequest;

/** Block types the agent may author by hand — the eight non-product types. */
export type AuthoredBlockType =
  | 'heading'
  | 'text'
  | 'button'
  | 'image'
  | 'divider'
  | 'spacer'
  | 'banner'
  | 'info-box';

/** A block authored by the agent — no `id` (the editor assigns one on insert). */
export interface AuthoredBlock {
  type: AuthoredBlockType;
  data: EmailBlockData;
}

/**
 * One edit operation applied to the draft. Discriminated on `op`; matches the
 * backend `emailEditOperationSchema`.
 */
export type EmailEditOperation =
  | { op: 'set-meta'; subject?: string; preheader?: string }
  | { op: 'add-block'; block: AuthoredBlock; index?: number }
  | { op: 'update-block'; id: string; block: AuthoredBlock }
  | { op: 'remove-block'; id: string }
  | { op: 'reorder-blocks'; orderedIds: string[] }
  | {
      op: 'add-product';
      itemId: string;
      variant: 'card' | 'spotlight';
      index?: number;
    }
  | { op: 'add-product-list'; itemIds: string[]; title?: string; index?: number };

/** Input of the client-executed `applyEmailEdits` tool. */
export interface ApplyEmailEditsInput {
  /** One short sentence describing the change, in the user's language. */
  summary: string;
  operations: EmailEditOperation[];
}

/** Result the client returns for an `applyEmailEdits` tool call. */
export interface ApplyEmailEditsOutput {
  ok: boolean;
  summary: string;
  /** Block count of the draft after the edits were applied. */
  blockCount: number;
  /** Human-readable notes for operations that could not be fully applied. */
  warnings?: string[];
}

/**
 * Tool set surfaced by the email-builder agent, typed for `useChat`.
 * `searchItems` / `getItem` run on the backend; `applyEmailEdits` runs here.
 */
export type EmailBuilderUITools = {
  applyEmailEdits: { input: ApplyEmailEditsInput; output: ApplyEmailEditsOutput };
  searchItems: { input: { query?: string; limit?: number }; output: unknown };
  getItem: { input: { itemId?: string }; output: unknown };
};

/**
 * UI message type for the email-builder conversation — pass to
 * `useChat<EmailBuilderUIMessage>()` for typed tool parts.
 */
export type EmailBuilderUIMessage = UIMessage<
  unknown,
  UIDataTypes,
  EmailBuilderUITools
>;

// ---------------------------------------------------------------------------
// Main EOS assistant — wire contract for the general chat endpoint
// (`POST /v1/ai/chat`), backing the global EOS Assistant slide-over.
// ---------------------------------------------------------------------------

/** Path of the streaming main-assistant chat endpoint. */
export const ASSISTANT_CHAT_PATH = '/v1/ai/chat';

// --- Tool result shapes ----------------------------------------------------
// The chat renders each backend tool's result as a rich card, so the outputs
// are typed field for field. Mirror the backend tool `execute` return values
// in `modules/ai/agents/assistant.tools.ts` — keep the two in sync.

/** A catalog item in a `searchItems` result list. */
export interface AssistantItemSummary {
  id: string;
  name: string;
  manufacturer: string | null;
  itemGroup: string | null;
  price: number | null;
  availableQuantity: number | null;
}

/** `searchItems` output — a compact list of catalog matches. */
export interface SearchItemsOutput {
  query: string;
  count: number;
  items: AssistantItemSummary[];
}

/** A catalog item's full detail, as returned by `getItem`. */
export interface AssistantItemDetail {
  id: string;
  name: string;
  manufacturer: string | null;
  itemGroup: string | null;
  price: number | null;
  movingAveragePrice: number | null;
  quantityOnStock: number | null;
  availableQuantity: number | null;
  quantityOrderedByCustomers: number | null;
  barCode: string | null;
}

/** `getItem` output — the item, or a not-found marker. */
export type GetItemOutput =
  | { found: true; item: AssistantItemDetail }
  | { found: false; itemId: string };

/** A business partner in a `queryBusinessPartners` result list. */
export interface AssistantPartnerSummary {
  id: number;
  name: string;
  type: string;
  /** Partner website, used to render a favicon — `null` when unknown. */
  website: string | null;
  totalOrders: number;
  openOrders: number;
  revenue: number;
  lastOrderDate: string | null;
  /** Days since the last order — `null` for partners who never ordered. */
  daysSinceLastOrder: number | null;
  /** The partner's own average gap between orders — `null` with < 2 orders. */
  avgOrderIntervalDays: number | null;
  /** `daysSinceLastOrder ÷ avgOrderIntervalDays` — `null` if no rhythm. */
  overdueRatio: number | null;
}

/** A field `queryBusinessPartners` can filter or sort on. */
export type BpQueryField =
  | 'name'
  | 'type'
  | 'totalOrders'
  | 'openOrders'
  | 'revenue'
  | 'lastOrderDate'
  | 'daysSinceLastOrder'
  | 'avgOrderIntervalDays'
  | 'overdueRatio';

/** One AND-combined filter condition of a `queryBusinessPartners` query. */
export interface BpQueryFilter {
  field: BpQueryField;
  op: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'isNull' | 'isNotNull';
  value?: string | number;
}

/** One sort key of a `queryBusinessPartners` query. */
export interface BpQuerySort {
  field: BpQueryField;
  dir: 'asc' | 'desc';
}

/** Input of the `queryBusinessPartners` tool — a composable partner query. */
export interface QueryBusinessPartnersInput {
  filters?: BpQueryFilter[];
  sort?: BpQuerySort[];
  limit?: number;
  /** `'mine'` (default) = the user's own sales-person book; `'all'` = wider. */
  scope?: 'mine' | 'all';
}

/**
 * `queryBusinessPartners` output — partners matching the composed query.
 * `count` is the returned page size; `totalMatched` is how many partners
 * matched the filters before the limit was applied.
 */
export interface QueryBusinessPartnersOutput {
  count: number;
  totalMatched: number;
  businessPartners: AssistantPartnerSummary[];
}

/** A business partner's master data, as returned by `getBusinessPartner`. */
export interface AssistantPartnerDetail {
  id: number;
  name: string;
  type: string;
  legalName: string | null;
  vatId: string | null;
  email: string | null;
  website: string | null;
  creditLimit: number | null;
  defaultContactPersonName: string | null;
}

/** `getBusinessPartner` output — the partner, or a not-found marker. */
export type GetBusinessPartnerOutput =
  | { found: true; businessPartner: AssistantPartnerDetail }
  | { found: false; businessPartnerId: number };

/** A sales order in a `getBusinessPartnerOrders` result list. */
export interface AssistantOrderSummary {
  docNum: number;
  date: string;
  dueDate: string | null;
  total: number;
  status: 'open' | 'closed';
  reference: string | null;
  items: string | null;
  lineCount: number;
}

/** `getBusinessPartnerOrders` output — recent orders, or a not-found marker. */
export type GetBusinessPartnerOrdersOutput =
  | {
      found: true;
      businessPartnerId: number;
      totalOrders: number;
      orders: AssistantOrderSummary[];
    }
  | { found: false; businessPartnerId: number };

/** One line of a sales order in a `getOrder` result. */
export interface AssistantOrderLine {
  lineNum: number;
  itemCode: string;
  description: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  currency: string | null;
  status: 'open' | 'closed';
  openQuantity: number | null;
  shipDate: string | null;
}

/** A sales order's full detail, as returned by `getOrder`. */
export interface AssistantOrderDetail {
  docNum: number;
  businessPartnerId: number;
  businessPartnerName: string;
  date: string;
  dueDate: string | null;
  total: number;
  vat: number | null;
  status: 'open' | 'closed';
  reference: string | null;
  comments: string | null;
  lineItems: AssistantOrderLine[];
}

/** `getOrder` output — the order with its lines, or a not-found marker. */
export type GetOrderOutput =
  | { found: true; order: AssistantOrderDetail }
  | { found: false; docNum: number };

/**
 * One contact employee in a `listBusinessPartnerContacts` result.
 *
 * `gender` is the SAP `SapBoGenderTypes` enum value (`gt_Female`, `gt_Male`,
 * `gt_Undefined`, `gt_Masked`) or `null`. `salutation` is the SAP `U_Anrede`
 * code (`'D'` = duzen, `'S'` = siezen) or `null`. The composer skill maps
 * the pair through the Du/Sie × Herr/Frau matrix — see
 * `apps/backend/src/modules/business-partners/CLAUDE.md`.
 */
export interface AssistantContactSummary {
  id: number;
  name: string;
  firstName: string;
  lastName: string;
  position: string | null;
  email: string | null;
  phone1: string | null;
  phone2: string | null;
  gender: string | null;
  salutation: string | null;
}

/** `listBusinessPartnerContacts` output — contacts of a BP, or not-found. */
export type ListBusinessPartnerContactsOutput =
  | {
      found: true;
      businessPartnerId: number;
      totalContacts: number;
      contacts: AssistantContactSummary[];
    }
  | { found: false; businessPartnerId: number };

// --- askUser (client-executed clarification tool) -------------------------

/** One option in an askUser question. */
export interface AskUserOption {
  label: string;
  description?: string;
}

/** One question presented to the user. */
export interface AskUserQuestion {
  question: string;
  options: AskUserOption[];
  multiSelect: boolean;
}

/** Input of the client-executed `askUser` tool. */
export interface AskUserInput {
  questions: AskUserQuestion[];
}

/** One answered question in the tool output. */
export interface AskUserAnswer {
  question: string;
  selected: string[];
}

/** Result the client returns for an `askUser` tool call. */
export interface AskUserOutput {
  answers: AskUserAnswer[];
}

// --- verteiler skill (Verteiler / contact-list management) ----------------
// Backend-executed tools surfaced by the `verteiler` skill on the main
// assistant. Mirror the backend `contact-list.skill.ts` — keep in sync.

/** One Verteiler in a `listContactLists` result. */
export interface AssistantContactListSummary {
  id: number;
  name: string;
  description: string | null;
  kind: 'customer' | 'supplier';
  memberCount: number;
  createdAt: string;
}

/** `listContactLists` output — the caller's own Verteiler. */
export interface ListContactListsOutput {
  count: number;
  contactLists: AssistantContactListSummary[];
}

/** One member returned by `createContactList`. */
export interface AssistantContactListMember {
  contactEmployeeId: number;
  name: string;
  email: string | null;
  businessPartnerId: number;
  businessPartnerName: string;
}

/** `createContactList` output — success carries the new list, error carries a reason. */
export type CreateContactListOutput =
  | {
      ok: true;
      contactList: {
        id: number;
        name: string;
        description: string | null;
        kind: 'customer' | 'supplier';
        memberCount: number;
        members: AssistantContactListMember[];
      };
    }
  | { ok: false; error: 'bad-request'; message: string };

/** `addContactsToContactList` output. */
export type AddContactsToContactListOutput =
  | {
      ok: true;
      addedCount: number;
      contactList: {
        id: number;
        name: string;
        kind: 'customer' | 'supplier';
        memberCount: number;
      };
    }
  | { ok: false; error: 'bad-request'; message: string }
  | { ok: false; error: 'not-found'; listId: number };

/** `addDefaultContactsToContactList` output. */
export type AddDefaultContactsToContactListOutput =
  | {
      ok: true;
      addedCount: number;
      scope: 'own' | 'all';
      contactList: {
        id: number;
        name: string;
        kind: 'customer' | 'supplier';
        memberCount: number;
      };
    }
  | { ok: false; error: 'forbidden'; message: string }
  | { ok: false; error: 'not-found'; listId: number };

// --- activateSkill (built-in backend meta-tool) ----------------------------

/** Input of the `activateSkill` meta-tool — the skill the agent wants to use. */
export interface ActivateSkillInput {
  name: string;
}

/** Output of the `activateSkill` meta-tool. */
export interface ActivateSkillOutput {
  activated: boolean;
  name: string;
  unlockedTools?: string[];
}

// --- message-compose skill (client-rendered composer) ----------------------
// Surfaced by the `message-compose` skill on the main assistant. The agent
// emits the JSON; the chat renders a composer card the user can hand off or
// copy. Mirrors the backend `messageComposeInputSchema` in
// `modules/ai/skills/message-compose.skill.ts` — keep the two in sync.

/** The message channel the composer card is built for. */
export type MessageComposeKind = 'email' | 'textMessage' | 'other';

/** One drafted variant of the message. */
export interface MessageComposeVariant {
  /** 2–4 word goal-oriented strategy label (e.g. "Hold firm"). */
  label: string;
  /** The message body. */
  body: string;
  /** Email subject line — only meaningful when `kind === 'email'`. */
  subject?: string;
}

/** The kind of EOS record an evidence entry references. */
export type MessageComposeEvidenceKind =
  | 'business-partner'
  | 'contact'
  | 'order'
  | 'item'
  | 'other';

/** One EOS record the draft is grounded in (looked up before composing). */
export interface MessageComposeEvidence {
  kind: MessageComposeEvidenceKind;
  /** Record id (BP id, order docNum, item article number, …) as a string. */
  id: string;
  /** Short human-readable note, e.g. "Telefonica DE — Kundennummer". */
  note?: string;
}

/**
 * Recipient identity threaded through from the AI's preflight contact
 * lookup. Used by the composer card to resolve `{{anrede}}` / `{{kontakt.*}}` /
 * `{{firma}}`. Omitted only for addressee-less drafts.
 */
export interface MessageComposeRecipient {
  firstName: string;
  lastName: string;
  /** Du/Sie SAP code (`'D'` / `'S'` / `null`). */
  salutation: 'D' | 'S' | null;
  /** SAP gender (`'gt_Male'` / `'gt_Female'` / `'gt_Undefined'` / `'gt_Masked'` / null). */
  gender: string | null;
  businessPartnerName: string;
}

/**
 * Sender identity threaded through from the system-prompt block. Used by
 * the composer card to resolve `{{grussformel}}` / `{{absender.*}}`.
 * Omitted only when no sender identity was injected (external accounts /
 * shared mailboxes) — the card then falls back to the team line.
 */
export interface MessageComposeSender {
  firstName: string;
  lastName: string;
  email: string | null;
}

/** Input of the client-rendered `message_compose_v1` tool. */
export interface MessageComposeInput {
  kind: MessageComposeKind;
  /** Short header naming the purpose of the message. */
  summary_title?: string;
  variants: MessageComposeVariant[];
  /**
   * The EOS records the draft is grounded in. Always present; empty only
   * for genuinely self-contained drafts. Rendered as chips under the card.
   */
  evidence: MessageComposeEvidence[];
  /** Recipient identity for variable resolution. */
  recipient?: MessageComposeRecipient;
  /** Sender identity for variable resolution. */
  sender?: MessageComposeSender;
}

/**
 * Tool set surfaced by the main EOS assistant, typed for `useChat`. The catalog
 * and business-partner tools are **backend-executed**; `askUser` and
 * `message_compose_v1` are **client-rendered** (the chat draws an interactive
 * card; askUser sends user answers back as the tool result, message_compose_v1
 * has no output and ends the agent loop). `activateSkill` is a backend-executed
 * meta-tool that unlocks a skill's tools for subsequent steps.
 * Mirrors the backend `AssistantTools` (`modules/ai/agents/assistant.tools.ts`)
 * and registered skills under `modules/ai/skills/` — keep in sync.
 */
export type AssistantUITools = {
  searchItems: {
    input: { query?: string; limit?: number };
    output: SearchItemsOutput;
  };
  getItem: { input: { itemId?: string }; output: GetItemOutput };
  queryBusinessPartners: {
    input: QueryBusinessPartnersInput;
    output: QueryBusinessPartnersOutput;
  };
  getBusinessPartner: {
    input: { businessPartnerId?: number };
    output: GetBusinessPartnerOutput;
  };
  getBusinessPartnerOrders: {
    input: { businessPartnerId?: number; limit?: number };
    output: GetBusinessPartnerOrdersOutput;
  };
  getOrder: { input: { docNum?: number }; output: GetOrderOutput };
  listBusinessPartnerContacts: {
    input: { businessPartnerId?: number };
    output: ListBusinessPartnerContactsOutput;
  };
  askUser: { input: AskUserInput; output: AskUserOutput };
  activateSkill: { input: ActivateSkillInput; output: ActivateSkillOutput };
  listContactLists: {
    input: Record<string, never>;
    output: ListContactListsOutput;
  };
  createContactList: {
    input: {
      kind?: 'customer' | 'supplier';
      name?: string;
      description?: string;
      contactEmployeeIds?: number[];
    };
    output: CreateContactListOutput;
  };
  addContactsToContactList: {
    input: { listId?: number; contactEmployeeIds?: number[] };
    output: AddContactsToContactListOutput;
  };
  addDefaultContactsToContactList: {
    input: {
      listId?: number;
      businessPartnerIds?: number[];
      scope?: 'own' | 'all';
    };
    output: AddDefaultContactsToContactListOutput;
  };
  // The tool is purely a rendering directive; the backend returns a trivial
  // `{ ok: true }` only so the conversation history stays valid (every tool
  // call must be paired with a result). The card is rendered as soon as
  // `input-available`.
  message_compose_v1: { input: MessageComposeInput; output: { ok: true } };
};

/**
 * Per-message metadata forwarded by the backend on `start` / `finish`. The
 * pricing component is computed server-side so this is what arrives directly.
 * Mirrors `AssistantMessageMetadata` in `apps/backend/src/modules/ai/agents/
 * assistant.agent.ts` — keep both in sync.
 */
export interface AssistantMessageMetadata {
  /** Persisted conversation id, emitted on `start`. Captured by the client on the first turn. */
  conversationId?: string;
  /** Persisted assistant message id, emitted on `finish`. Keys the 👍/👎 controls. */
  messageId?: string;
  model?: string;
  createdAt?: number;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  cacheReadTokens?: number;
  reasoningTokens?: number;
  costUsd?: number;
  /** Persisted user rating. Hydrated when loading a stored conversation. */
  rating?: 'up' | 'down' | null;
}

/** Rating values accepted by `POST /v1/ai/messages/:id/rating`. */
export type AssistantMessageRating = 'up' | 'down' | null;

/** Summary row in `GET /v1/ai/conversations`. */
export interface AssistantConversationSummary {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string;
}

/** One persisted message in `GET /v1/ai/conversations/:id`. */
export interface AssistantConversationMessage {
  id: string;
  clientMessageId: string | null;
  role: 'user' | 'assistant' | 'system';
  parts: AssistantUIMessage['parts'];
  metadata: AssistantMessageMetadata | null;
  rating: AssistantMessageRating;
  ratedAt: string | null;
  createdAt: string;
}

/** Full conversation payload returned by `GET /v1/ai/conversations/:id`. */
export interface AssistantConversationDetail
  extends AssistantConversationSummary {
  messages: AssistantConversationMessage[];
}

/**
 * UI message type for the EOS assistant conversation — pass to
 * `useChat<AssistantUIMessage>()` for typed tool parts and metadata.
 */
export type AssistantUIMessage = UIMessage<
  AssistantMessageMetadata,
  UIDataTypes,
  AssistantUITools
>;
